import {
    differenceInDays,
    endOfWeek,
    format,
    min,
    startOfMonth,
    startOfWeek,
    subDays,
    subMonths,
} from 'date-fns';

/**
 * Utilitários do dashboard de relatórios de limpeza (normalização da API, rótulos, séries).
 */

function pad2(n) {
    return String(n).padStart(2, '0');
}

/**
 * Instante em ISO 8601 com offset do fuso local (ex.: 2026-03-17T00:00:00-03:00).
 */
function localDateToOffsetISO(date) {
    const y = date.getFullYear();
    const mo = pad2(date.getMonth() + 1);
    const d = pad2(date.getDate());
    const h = pad2(date.getHours());
    const mi = pad2(date.getMinutes());
    const s = pad2(date.getSeconds());
    const offsetMin = -date.getTimezoneOffset();
    const sign = offsetMin >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMin);
    const oh = pad2(Math.floor(abs / 60));
    const om = pad2(abs % 60);
    return `${y}-${mo}-${d}T${h}:${mi}:${s}${sign}${oh}:${om}`;
}

/**
 * A partir de YYYY-MM-DD (input type="date"), monta o intervalo para a API:
 * início às 00:00:00 do primeiro dia e fim às 23:59:59 do último dia, no fuso local do navegador.
 */
export function buildCleaningReportApiTimeRange(startYmd, endYmd) {
    const parseYmd = ymd => {
        if (!ymd || typeof ymd !== 'string') return null;
        const p = ymd.split('-').map(x => parseInt(x, 10));
        if (p.length !== 3 || p.some(n => Number.isNaN(n))) return null;
        return { y: p[0], m: p[1], d: p[2] };
    };

    const startParts = parseYmd(startYmd);
    const endParts = parseYmd(endYmd);

    if (!startParts || !endParts) {
        return {
            start_time: `${startYmd}T00:00:00`,
            end_time: `${endYmd}T23:59:59`,
        };
    }

    const startDate = new Date(
        startParts.y,
        startParts.m - 1,
        startParts.d,
        0,
        0,
        0,
        0
    );
    const endDate = new Date(
        endParts.y,
        endParts.m - 1,
        endParts.d,
        23,
        59,
        59,
        0
    );

    return {
        start_time: localDateToOffsetISO(startDate),
        end_time: localDateToOffsetISO(endDate),
    };
}

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;

/**
 * Formata duração total em segundos para leitura humana (s / min / h).
 */
export function formatCleaningDuration(seconds) {
    const s = Math.max(0, Math.floor(Number(seconds) || 0));
    if (s < SECONDS_PER_MINUTE) {
        return `${s} s`;
    }
    if (s < SECONDS_PER_HOUR) {
        const m = Math.floor(s / SECONDS_PER_MINUTE);
        const rs = s % SECONDS_PER_MINUTE;
        return rs ? `${m} min ${rs} s` : `${m} min`;
    }
    const h = Math.floor(s / SECONDS_PER_HOUR);
    const r = s % SECONDS_PER_HOUR;
    const m = Math.floor(r / SECONDS_PER_MINUTE);
    const rs = r % SECONDS_PER_MINUTE;
    const parts = [`${h} h`];
    if (m) parts.push(`${m} min`);
    if (rs) parts.push(`${rs} s`);
    return parts.join(' ');
}

/**
 * Formata duração total sem exibir os segundos (usado nos cards de resumo).
 */
export function formatCleaningDurationNoSeconds(seconds) {
    const s = Math.max(0, Math.floor(Number(seconds) || 0));
    if (s < SECONDS_PER_MINUTE) {
        return '< 1 min';
    }
    if (s < SECONDS_PER_HOUR) {
        const m = Math.floor(s / SECONDS_PER_MINUTE);
        return `${m} min`;
    }
    const h = Math.floor(s / SECONDS_PER_HOUR);
    const r = s % SECONDS_PER_HOUR;
    const m = Math.floor(r / SECONDS_PER_MINUTE);
    const parts = [`${h} h`];
    if (m) parts.push(`${m} min`);
    return parts.join(' ');
}

/**
 * Momento de início da tarefa (Pudu: `start_time` em Unix). Não usar `create_time` (sync/registro).
 */
export function getCleaningReportTaskStartTime(row) {
    if (!row || typeof row !== 'object') return undefined;
    if (row.start_time !== undefined && row.start_time !== null) {
        return row.start_time;
    }
    return undefined;
}

/**
 * Chave de dia YYYY-MM-DD a partir de `start_time` (Unix) ou string de data/hora.
 */
export function getCleaningReportDayKey(createTime) {
    if (createTime == null || createTime === '') {
        return '—';
    }
    if (typeof createTime === 'number' && Number.isFinite(createTime)) {
        const ms = createTime > 1e12 ? createTime : createTime * 1000;
        const date = new Date(ms);
        if (Number.isNaN(date.getTime())) {
            return '—';
        }
        const y = date.getFullYear();
        const mo = pad2(date.getMonth() + 1);
        const d = pad2(date.getDate());
        return `${y}-${mo}-${d}`;
    }
    const s = String(createTime).trim();
    if (!s) {
        return '—';
    }

    let head;
    if (s.includes('T')) {
        head = s.split('T')[0].trim();
    } else {
        head = s.split(/\s+/)[0].trim();
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(head)) {
        return head;
    }

    const prefix = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (prefix) {
        return `${prefix[1]}-${prefix[2]}-${prefix[3]}`;
    }

    return '—';
}

/**
 * Rótulo curto para eixo do gráfico a partir da chave YYYY-MM-DD (calendário estável).
 */
export function cleaningReportYmdToChartLabel(dayKey, localeFormat) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) {
        return dayKey;
    }
    const [yStr, moStr, dStr] = dayKey.split('-');
    const localCal = new Date(
        Number(yStr),
        parseInt(moStr, 10) - 1,
        parseInt(dStr, 10)
    );
    if (Number.isNaN(localCal.getTime())) {
        return dayKey;
    }
    return localCal.toLocaleDateString(localeFormat, {
        month: '2-digit',
        day: '2-digit',
    });
}

/** Rótulo de mês para eixo (YYYY-MM). */
function cleaningReportYmToChartLabel(ymKey, localeFormat) {
    if (!/^\d{4}-\d{2}$/.test(ymKey)) {
        return ymKey;
    }
    const [yStr, moStr] = ymKey.split('-');
    const localCal = new Date(Number(yStr), parseInt(moStr, 10) - 1, 1);
    if (Number.isNaN(localCal.getTime())) {
        return ymKey;
    }
    return localCal.toLocaleDateString(localeFormat, {
        month: 'short',
        year: 'numeric',
    });
}

/**
 * Data de criação: dd/mm/aaaa a partir da parte data do create_time da API (sem adiantar/atrasar por fuso).
 */
export function formatCleaningReportCreateDate(createTime) {
    if (createTime == null || String(createTime).trim() === '') {
        return '—';
    }
    const key = getCleaningReportDayKey(createTime);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) {
        return key === '—' ? '—' : String(createTime).trim();
    }
    const [y, m, d] = key.split('-');
    return `${d}/${m}/${y}`;
}
/**
 * Extrai horário literal de create_time (após a data), sem converter fuso.
 * Ex.: "2023-08-31 23:52:08" → "23:52:08"; ISO após T → mesma lógica.
 */
function extractCleaningReportTimePart(createTime) {
    if (createTime == null || typeof createTime === 'number') {
        return '';
    }
    const str = String(createTime).trim();
    if (!str) {
        return '';
    }

    if (str.includes('T')) {
        const afterT = str.split('T')[1] || '';
        const m = /^(\d{2}):(\d{2})(?::(\d{2}))?/.exec(afterT);
        if (m) {
            return m[3] !== undefined
                ? `${m[1]}:${m[2]}:${m[3]}`
                : `${m[1]}:${m[2]}`;
        }
        return '';
    }

    const spaceIdx = str.search(/\s/);
    if (spaceIdx < 0) {
        return '';
    }
    const rest = str.slice(spaceIdx + 1).trim();
    const m = /^(\d{2}):(\d{2})(?::(\d{2}))?/.exec(rest);
    if (m) {
        return m[3] !== undefined
            ? `${m[1]}:${m[2]}:${m[3]}`
            : `${m[1]}:${m[2]}`;
    }
    return '';
}

/**
 * Timestamp em ms para ordenar por data de criação.
 * Valores inválidos → -Infinity (ficam no fim quando a ordem é decrescente).
 */
function createTimeToSortMs(createTime) {
    if (createTime == null || createTime === '') {
        return Number.NEGATIVE_INFINITY;
    }
    if (typeof createTime === 'number' && Number.isFinite(createTime)) {
        return createTime > 1e12 ? createTime : createTime * 1000;
    }
    const s = String(createTime).trim();
    if (!s) {
        return Number.NEGATIVE_INFINITY;
    }
    const withT = /^\d{4}-\d{2}-\d{2}\s/.test(s) ? s.replace(/\s+/, 'T') : s;
    let ms = Date.parse(withT);
    if (!Number.isNaN(ms)) {
        return ms;
    }
    const day = getCleaningReportDayKey(createTime);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
        return Number.NEGATIVE_INFINITY;
    }
    const timePart = extractCleaningReportTimePart(createTime) || '00:00:00';
    ms = Date.parse(`${day}T${timePart}`);
    return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
}

/**
 * Lista ordenada por start_time decrescente (início da tarefa, mais recente primeiro).
 */
export function sortCleaningReportListByCreateTimeDesc(list) {
    if (!Array.isArray(list) || list.length === 0) {
        return Array.isArray(list) ? [...list] : [];
    }
    return [...list].sort(
        (a, b) =>
            createTimeToSortMs(getCleaningReportTaskStartTime(b)) -
            createTimeToSortMs(getCleaningReportTaskStartTime(a))
    );
}

/**
 * Normaliza resposta do backend / Pudu para { total, offset, limit, list }.
 */
export function normalizeCleaningReportResponse(body) {
    if (!body || typeof body !== 'object') {
        return { total: 0, offset: 0, limit: 0, list: [] };
    }
    if (body.error && body.code) {
        return {
            total: 0,
            offset: 0,
            limit: 0,
            list: [],
            apiError: body,
        };
    }

    let inner = body;
    if (
        body.data != null &&
        typeof body.data === 'object' &&
        !Array.isArray(body.data)
    ) {
        inner = body.data;
    }

    let list = [];
    if (Array.isArray(inner.list)) {
        list = inner.list;
    } else if (Array.isArray(body.list)) {
        list = body.list;
    }

    let totalRaw;
    if (inner.total !== undefined) {
        totalRaw = inner.total;
    } else if (body.total !== undefined) {
        totalRaw = body.total;
    } else {
        totalRaw = list.length;
    }
    const total = Number(totalRaw) || 0;

    let offsetRaw;
    if (inner.offset !== undefined) {
        offsetRaw = inner.offset;
    } else if (body.offset !== undefined) {
        offsetRaw = body.offset;
    } else {
        offsetRaw = 0;
    }
    const offset = Number(offsetRaw) || 0;

    let limitRaw;
    if (inner.limit !== undefined) {
        limitRaw = inner.limit;
    } else if (body.limit !== undefined) {
        limitRaw = body.limit;
    } else {
        limitRaw = list.length;
    }
    const limit = Number(limitRaw) || 0;

    return {
        total,
        offset,
        limit,
        list: sortCleaningReportListByCreateTimeDesc(list),
    };
}

/**
 * Intervalo padrão para painel ao vivo (últimos 7 dias, fim = hoje).
 */
export function defaultLiveCleaningReportDateRange() {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    const ymd = d =>
        `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    return {
        startDate: ymd(start),
        endDate: ymd(end),
    };
}

/**
 * Semana civil atual (segunda → hoje), fuso local. Para preview no dashboard.
 */
export function currentWeekCleaningReportDateRange(now = new Date()) {
    const start = startOfWeek(now, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
    const end = min([weekEnd, now]);
    return {
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(end, 'yyyy-MM-dd'),
    };
}

/**
 * Doze meses corridos (início do mês há 11 meses → hoje). Para gráfico por mês no dashboard.
 */
export function lastTwelveMonthsCleaningReportDateRange(now = new Date()) {
    const start = startOfMonth(subMonths(now, 11));
    return {
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(now, 'yyyy-MM-dd'),
    };
}

/**
 * Mês civil atual (dia 1 → hoje), fuso local. Para preview diário no dashboard.
 */
export function currentMonthToDateCleaningReportDateRange(now = new Date()) {
    const start = startOfMonth(now);
    return {
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(now, 'yyyy-MM-dd'),
    };
}

/**
 * Trinta dias corridos (hoje e os 29 anteriores), fuso local.
 */
export function last30DaysCleaningReportDateRange(now = new Date()) {
    return {
        startDate: format(subDays(now, 29), 'yyyy-MM-dd'),
        endDate: format(now, 'yyyy-MM-dd'),
    };
}

/** `code` em `robots_errors[]` (API / CleaningReportModule) → chave LocaleMessage. */
const CLEANING_REPORT_ROBOT_ERROR_MSG_BY_CODE = {
    invalid_settings:
        'page.robots.cleaning_report.robot_error.invalid_settings',
    unknown_error: 'page.robots.cleaning_report.robot_error.unknown_error',
    robot_not_found: 'page.robots.cleaning_report.robot_error.robot_not_found',
    invalid_query: 'page.robots.cleaning_report.robot_error.invalid_query',
};

/**
 * @param {string|null|undefined} code
 * @returns {string|null} chave de mensagem ou null se não houver tradução fixa para o código
 */
export function getCleaningReportRobotErrorMsgKey(code) {
    if (code == null || code === '') return null;
    const k = String(code).trim();
    return CLEANING_REPORT_ROBOT_ERROR_MSG_BY_CODE[k] || null;
}

/**
 * Resposta agregada GET /robots/cleaning-report/cc1 (lista multi-robô + metadados).
 */
export function normalizeAggregatedCleaningReportResponse(body) {
    if (!body || typeof body !== 'object') {
        return {
            list: [],
            total: 0,
            offset: 0,
            limit: 0,
            robots_queried: 0,
            robots_errors: [],
        };
    }
    if (body.error && body.code) {
        return {
            list: [],
            total: 0,
            offset: 0,
            limit: 0,
            robots_queried: 0,
            robots_errors: [],
            apiError: body,
        };
    }
    const list = Array.isArray(body.list) ? body.list : [];
    const sorted = sortCleaningReportListByCreateTimeDesc(list);
    const total = Number(body.total) || sorted.length;
    return {
        list: sorted,
        total,
        offset: Number(body.offset) || 0,
        limit: Number(body.limit) || sorted.length,
        robots_queried: Number(body.robots_queried) || 0,
        robots_errors: Array.isArray(body.robots_errors)
            ? body.robots_errors
            : [],
        applications: body.applications || {},
        calculated_metrics: body.calculated_metrics || null,
    };
}

/**
 * Data e hora de criação: dd/mm/aaaa HH:mm:ss (valores literais da API quando em string).
 */
export function formatCleaningReportCreateDateTime(createTime) {
    if (createTime == null || String(createTime).trim() === '') {
        return '—';
    }
    if (typeof createTime === 'number' && Number.isFinite(createTime)) {
        const ms = createTime > 1e12 ? createTime : createTime * 1000;
        const date = new Date(ms);
        if (Number.isNaN(date.getTime())) {
            return '—';
        }
        const d = pad2(date.getDate());
        const mo = pad2(date.getMonth() + 1);
        const y = date.getFullYear();
        const h = pad2(date.getHours());
        const mi = pad2(date.getMinutes());
        const sec = pad2(date.getSeconds());
        return `${d}/${mo}/${y} ${h}:${mi}:${sec}`;
    }

    const dateStr = formatCleaningReportCreateDate(createTime);
    if (dateStr === '—' || !/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
        return dateStr;
    }

    const timePart = extractCleaningReportTimePart(createTime);
    return timePart ? `${dateStr} ${timePart}` : dateStr;
}

/**
 * Modo de limpeza (Pudu): 1 = lavagem molhada, 2 = lavagem a seco.
 * Validar com a doc “Cleaning Report - Details”: open.pudutech.com (cloud-api).
 */
export function getCleaningModeLabel(mode, localeCode = 'pt_BR') {
    const m = Number(mode);
    const en = localeCode === 'en_US';
    if (m === 1) return en ? 'Wet cleaning' : 'Lavagem molhada';
    if (m === 2) return en ? 'Dry cleaning' : 'Lavagem a seco';
    return mode != null ? String(mode) : '?';
}

/** Códigos de status de tarefa de limpeza (Pudu). */
const CLEANING_TASK_STATUS_CODES = [0, 1, 2, 3, 4, 5, 6];

/**
 * Status da tarefa de limpeza (Pudu): 0–6 conforme API.
 * Tabela oficial: documentação “Cleaning Report - Details” (Pudu Open Platform).
 */
export function getCleaningTaskStatusLabel(status, localeCode = 'pt_BR') {
    const s = Number(status);
    const en = localeCode === 'en_US';
    const labels = en
        ? {
              0: 'Not started / Default',
              1: 'Cleaning',
              2: 'Task paused',
              3: 'Task interrupted',
              4: 'Task ended',
              5: 'Task exception',
              6: 'Task canceled',
          }
        : {
              0: 'Não iniciada / Padrão',
              1: 'Em limpeza',
              2: 'Tarefa pausada',
              3: 'Tarefa interrompida',
              4: 'Tarefa concluída',
              5: 'Exceção na tarefa',
              6: 'Tarefa cancelada',
          };
    if (Object.prototype.hasOwnProperty.call(labels, s)) return labels[s];
    return status != null ? String(status) : '?';
}

/** Mapa status numérico → rótulo (pizza por status). */
export function getCleaningStatusLabelMap(localeCode = 'pt_BR') {
    return CLEANING_TASK_STATUS_CODES.reduce((acc, code) => {
        acc[code] = getCleaningTaskStatusLabel(code, localeCode);
        return acc;
    }, {});
}

/**
 * Série para gráfico de área: tempo total de limpeza por dia.
 * Escala o eixo Y em segundos, minutos ou horas conforme o maior total diário.
 * Cada ponto: { date, value (escala), secondsTotal (bruto) }.
 */
export function buildAreaSeriesFromList(list, localeFormat = 'pt-BR') {
    if (!list || !list.length) {
        return { data: [], unit: 'seconds' };
    }
    const byDay = {};
    list.forEach(item => {
        const day = getCleaningReportDayKey(
            getCleaningReportTaskStartTime(item)
        );
        if (day === '—') return;
        byDay[day] = (byDay[day] || 0) + (Number(item.clean_time) || 0);
    });
    const sortedDays = Object.keys(byDay).sort();
    const points = sortedDays.map(day => ({
        date: cleaningReportYmdToChartLabel(day, localeFormat),
        secondsTotal: byDay[day],
    }));
    const maxSec = Math.max(0, ...points.map(p => p.secondsTotal));
    let unit = 'seconds';
    let divisor = 1;
    if (maxSec >= SECONDS_PER_HOUR) {
        unit = 'hours';
        divisor = SECONDS_PER_HOUR;
    } else if (maxSec >= SECONDS_PER_MINUTE) {
        unit = 'minutes';
        divisor = SECONDS_PER_MINUTE;
    }
    const data = points.map(p => ({
        date: p.date,
        value: Math.round((p.secondsTotal / divisor) * 100) / 100,
        secondsTotal: p.secondsTotal,
    }));
    return { data, unit };
}

/**
 * Série para gráfico de área: tempo total de limpeza agregado por mês (YYYY-MM).
 * Sempre 12 pontos (doze meses corridos até `referenceDate`), com 0 nos meses sem dados.
 * Mesma escala de unidade que {@link buildAreaSeriesFromList}.
 */
export function buildAreaSeriesByMonthFromList(
    list,
    localeFormat = 'pt-BR',
    referenceDate = new Date()
) {
    const monthKeys = [];
    for (let i = 11; i >= 0; i -= 1) {
        const d = startOfMonth(subMonths(referenceDate, i));
        monthKeys.push(format(d, 'yyyy-MM'));
    }

    const byMonth = {};
    (list || []).forEach(item => {
        const day = getCleaningReportDayKey(
            getCleaningReportTaskStartTime(item)
        );
        if (day === '—' || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return;
        const ym = day.slice(0, 7);
        byMonth[ym] = (byMonth[ym] || 0) + (Number(item.clean_time) || 0);
    });

    const points = monthKeys.map(ym => ({
        date: cleaningReportYmToChartLabel(ym, localeFormat),
        secondsTotal: byMonth[ym] || 0,
    }));
    const maxSec = Math.max(0, ...points.map(p => p.secondsTotal));
    let unit = 'seconds';
    let divisor = 1;
    if (maxSec >= SECONDS_PER_HOUR) {
        unit = 'hours';
        divisor = SECONDS_PER_HOUR;
    } else if (maxSec >= SECONDS_PER_MINUTE) {
        unit = 'minutes';
        divisor = SECONDS_PER_MINUTE;
    }
    const data = points.map(p => ({
        date: p.date,
        value: Math.round((p.secondsTotal / divisor) * 100) / 100,
        secondsTotal: p.secondsTotal,
    }));
    return { data, unit };
}

/**
 * Série para gráfico de área: tempo de limpeza por dia no mês atual (dia 1 → hoje).
 * Um ponto por dia com 0 quando não há dados.
 */
export function buildAreaSeriesByDayInCurrentMonthFromList(
    list,
    localeFormat = 'pt-BR',
    referenceDate = new Date()
) {
    const y = referenceDate.getFullYear();
    const mo = referenceDate.getMonth() + 1;
    const todayDom = referenceDate.getDate();
    const ymPrefix = `${y}-${pad2(mo)}-`;

    const dayKeys = [];
    for (let d = 1; d <= todayDom; d += 1) {
        dayKeys.push(`${ymPrefix}${pad2(d)}`);
    }

    const byDay = {};
    (list || []).forEach(item => {
        const day = getCleaningReportDayKey(
            getCleaningReportTaskStartTime(item)
        );
        if (day === '—' || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return;
        if (!day.startsWith(ymPrefix)) return;
        byDay[day] = (byDay[day] || 0) + (Number(item.clean_time) || 0);
    });

    const points = dayKeys.map(day => ({
        date: cleaningReportYmdToChartLabel(day, localeFormat),
        secondsTotal: byDay[day] || 0,
    }));
    const maxSec = Math.max(0, ...points.map(p => p.secondsTotal));
    let unit = 'seconds';
    let divisor = 1;
    if (maxSec >= SECONDS_PER_HOUR) {
        unit = 'hours';
        divisor = SECONDS_PER_HOUR;
    } else if (maxSec >= SECONDS_PER_MINUTE) {
        unit = 'minutes';
        divisor = SECONDS_PER_MINUTE;
    }
    const data = points.map(p => ({
        date: p.date,
        value: Math.round((p.secondsTotal / divisor) * 100) / 100,
        secondsTotal: p.secondsTotal,
    }));
    return { data, unit };
}

/**
 * Série por dia: últimos 30 dias (inclusive hoje), com 0 nos dias sem dados.
 */
export function buildAreaSeriesByDayLast30DaysFromList(
    list,
    localeFormat = 'pt-BR',
    referenceDate = new Date()
) {
    const dayKeys = [];
    for (let i = 29; i >= 0; i -= 1) {
        dayKeys.push(format(subDays(referenceDate, i), 'yyyy-MM-dd'));
    }
    const daySet = new Set(dayKeys);

    const byDay = {};
    (list || []).forEach(item => {
        const day = getCleaningReportDayKey(
            getCleaningReportTaskStartTime(item)
        );
        if (day === '—' || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return;
        if (!daySet.has(day)) return;
        byDay[day] = (byDay[day] || 0) + (Number(item.clean_time) || 0);
    });

    const points = dayKeys.map(day => ({
        date: cleaningReportYmdToChartLabel(day, localeFormat),
        secondsTotal: byDay[day] || 0,
    }));
    const maxSec = Math.max(0, ...points.map(p => p.secondsTotal));
    let unit = 'seconds';
    let divisor = 1;
    if (maxSec >= SECONDS_PER_HOUR) {
        unit = 'hours';
        divisor = SECONDS_PER_HOUR;
    } else if (maxSec >= SECONDS_PER_MINUTE) {
        unit = 'minutes';
        divisor = SECONDS_PER_MINUTE;
    }
    const data = points.map(p => ({
        date: p.date,
        value: Math.round((p.secondsTotal / divisor) * 100) / 100,
        secondsTotal: p.secondsTotal,
    }));
    return { data, unit };
}

/**
 * Por dia: soma de clean_area (área efetivamente limpa) vs soma de task_area (área planejada da tarefa).
 * Permite comparar desempenho diário.
 */
export function buildCleanAreaVsTaskAreaByDay(
    list,
    localeFormat = 'pt-BR',
    periodDates = null
) {
    if (!list || !list.length) return [];

    let isSingleDay = false;
    if (periodDates && periodDates.startDate && periodDates.endDate) {
        if (periodDates.startDate === periodDates.endDate) {
            isSingleDay = true;
        }
    }

    if (isSingleDay) {
        const byHour = new Array(24)
            .fill(null)
            .map(() => ({ clean_area: 0, task_area: 0 }));
        list.forEach(item => {
            // eslint-disable-next-line no-use-before-define
            const h = extractHourFromCreateTime(
                getCleaningReportTaskStartTime(item)
            );
            if (h == null) return;
            byHour[h].clean_area += Number(item.clean_area) || 0;
            byHour[h].task_area += Number(item.task_area) || 0;
        });

        return byHour.map((area, hour) => ({
            date: `${pad2(hour)}h`,
            clean_area: Math.round(area.clean_area * 100) / 100,
            task_area: Math.round(area.task_area * 100) / 100,
        }));
    }

    const byDay = {};
    list.forEach(item => {
        const day = getCleaningReportDayKey(
            getCleaningReportTaskStartTime(item)
        );
        if (day === '—') return;
        if (!byDay[day]) {
            byDay[day] = { clean_area: 0, task_area: 0 };
        }
        byDay[day].clean_area += Number(item.clean_area) || 0;
        byDay[day].task_area += Number(item.task_area) || 0;
    });
    const sortedDays = Object.keys(byDay).sort();
    return sortedDays.map(day => {
        const { clean_area: ca, task_area: ta } = byDay[day];
        return {
            date: cleaningReportYmdToChartLabel(day, localeFormat),
            clean_area: Math.round(ca * 100) / 100,
            task_area: Math.round(ta * 100) / 100,
        };
    });
}

/**
 * Hora 0–23 extraída de create_time (literal na string; timestamp numérico em UTC).
 */
export function extractHourFromCreateTime(createTime) {
    if (createTime == null) {
        return null;
    }
    if (typeof createTime === 'number' && Number.isFinite(createTime)) {
        const ms = createTime > 1e12 ? createTime : createTime * 1000;
        const d = new Date(ms);
        if (Number.isNaN(d.getTime())) {
            return null;
        }
        return d.getHours();
    }
    const s = String(createTime).trim();
    if (!s) {
        return null;
    }
    if (s.includes('T')) {
        const tail = (s.split('T')[1] || '').replace(/[Zz].*$/, '').trim();
        const m = /^(\d{2})/.exec(tail);
        if (m) {
            const h = parseInt(m[1], 10);
            return h >= 0 && h <= 23 ? h : null;
        }
    }
    const spaceIdx = s.search(/\s/);
    if (spaceIdx < 0) {
        return null;
    }
    const rest = s.slice(spaceIdx + 1).trim();
    const m2 = /^(\d{2})/.exec(rest);
    if (m2) {
        const h = parseInt(m2[1], 10);
        return h >= 0 && h <= 23 ? h : null;
    }
    return null;
}

/**
 * Soma de clean_area por hora do dia (0h–23h) conforme start_time da tarefa.
 */
export function buildCleanAreaByHourOfDay(list) {
    const byHour = new Array(24).fill(0);
    if (list && list.length) {
        list.forEach(item => {
            const h = extractHourFromCreateTime(
                getCleaningReportTaskStartTime(item)
            );
            if (h == null) {
                return;
            }
            byHour[h] += Number(item.clean_area) || 0;
        });
    }
    return byHour.map((area, hour) => ({
        hour,
        label: `${pad2(hour)}h`,
        clean_area: Math.round(area * 100) / 100,
    }));
}

function parseCleaningReportYmd(ymd) {
    if (!ymd || typeof ymd !== 'string') return null;
    const parts = ymd.split('-').map(x => parseInt(x, 10));
    if (parts.length !== 3 || parts.some(n => Number.isNaN(n))) return null;
    return new Date(parts[0], parts[1] - 1, parts[2]);
}

/** Dias no intervalo do filtro de período (startDate → endDate, inclusive). */
export function getCleaningReportPeriodDaysCount(periodDates) {
    if (!periodDates?.startDate || !periodDates?.endDate) return 1;
    const start = parseCleaningReportYmd(periodDates.startDate);
    const end = parseCleaningReportYmd(periodDates.endDate);
    if (!start || !end || end < start) return 1;
    return differenceInDays(end, start) + 1;
}

/** O filtro cobre pelo menos um mês de calendário (ex.: 15/01 → 15/02). */
export function cleaningReportFilterCoversAtLeastOneMonth(periodDates) {
    if (!periodDates?.startDate || !periodDates?.endDate) return false;
    const start = parseCleaningReportYmd(periodDates.startDate);
    const end = parseCleaningReportYmd(periodDates.endDate);
    if (!start || !end || end < start) return false;
    const oneMonthAfterStart = new Date(
        start.getFullYear(),
        start.getMonth() + 1,
        start.getDate()
    );
    return end >= oneMonthAfterStart;
}

/** Índices fixos: 0 horas, 1 dias da semana, 2 dias, 3 meses. */
export const CLEANING_REPORT_CHART_PERIOD_INDICES = [0, 1, 2, 3];

/** Se a opção do seletor de métrica analisada deve aparecer para o filtro atual. */
export function isCleaningReportChartPeriodVisible(
    periodIdx,
    periodDates,
    daysCount
) {
    if (periodIdx === 0) return true;
    if (periodIdx === 2) return daysCount >= 2;
    if (periodIdx === 1) return daysCount >= 7;
    if (periodIdx === 3) {
        return cleaningReportFilterCoversAtLeastOneMonth(periodDates);
    }
    return false;
}

export function getVisibleCleaningReportChartPeriodIndices(
    periodDates,
    daysCount
) {
    return CLEANING_REPORT_CHART_PERIOD_INDICES.filter(idx =>
        isCleaningReportChartPeriodVisible(idx, periodDates, daysCount)
    );
}

/** Índice padrão do seletor entre as opções visíveis. */
export function getCleaningReportDefaultPeriodIdx(periodDates, daysCount) {
    const visible = getVisibleCleaningReportChartPeriodIndices(
        periodDates,
        daysCount
    );
    let preferred = 0;
    if (cleaningReportFilterCoversAtLeastOneMonth(periodDates)) preferred = 3;
    else if (daysCount >= 7) preferred = 2;
    else if (daysCount >= 2) preferred = 2;
    else preferred = 0;

    if (visible.includes(preferred)) return preferred;
    return visible[visible.length - 1] ?? 0;
}

/** Mantém a seleção do usuário ou volta ao padrão se a opção deixou de ser válida. */
export function resolveCleaningReportChartPeriodIdx(
    selectedPeriodIdx,
    periodDates,
    daysCount
) {
    const defaultIdx = getCleaningReportDefaultPeriodIdx(
        periodDates,
        daysCount
    );
    if (selectedPeriodIdx == null) return defaultIdx;
    const idx = Number(selectedPeriodIdx);
    if (
        Number.isNaN(idx) ||
        !isCleaningReportChartPeriodVisible(idx, periodDates, daysCount)
    ) {
        return defaultIdx;
    }
    return idx;
}

function scaleCleanTimeChartPoints(points) {
    const maxSec = Math.max(0, ...points.map(p => p.secondsTotal));
    let unit = 'seconds';
    let divisor = 1;
    if (maxSec >= SECONDS_PER_HOUR) {
        unit = 'hours';
        divisor = SECONDS_PER_HOUR;
    } else if (maxSec >= SECONDS_PER_MINUTE) {
        unit = 'minutes';
        divisor = SECONDS_PER_MINUTE;
    }
    const data = points.map(p => ({
        name: p.name,
        value: Math.round((p.secondsTotal / divisor) * 100) / 100,
        secondsTotal: p.secondsTotal,
    }));
    return { data, unit };
}

export function getCleaningReportWeekDayLabels(localeFormat) {
    const formatter = new Intl.DateTimeFormat(localeFormat, {
        weekday: 'short',
    });
    return Array.from({ length: 7 }, (_, i) =>
        formatter.format(new Date(2024, 0, 7 + i))
    );
}

export function getCleaningReportMonthLabels(localeFormat) {
    const formatter = new Intl.DateTimeFormat(localeFormat, { month: 'short' });
    return Array.from({ length: 12 }, (_, i) =>
        formatter.format(new Date(2024, i, 1))
    );
}

const CLEAN_TIME_CHART_LEGEND_LABELS = {
    pt_BR: 'Tempo de limpeza',
    en_US: 'Cleaning time',
    en_GB: 'Cleaning time',
    es_ES: 'Tiempo de limpieza',
};

export function getCleanTimeChartLegendLabel(localeCode = 'pt_BR') {
    return (
        CLEAN_TIME_CHART_LEGEND_LABELS[localeCode] ||
        CLEAN_TIME_CHART_LEGEND_LABELS.pt_BR
    );
}

/** Formata ticks do eixo Y do gráfico de tempo de limpeza. */
export function formatCleanTimeChartAxisTick(v, unit) {
    if (v == null || Number.isNaN(Number(v))) return '';
    const n = Number(v);
    if (unit === 'seconds') return `${Math.round(n)} s`;
    if (unit === 'minutes') {
        return Number.isInteger(n) ? `${n} min` : `${n.toFixed(1)} min`;
    }
    return Number.isInteger(n) ? `${n} h` : `${n.toFixed(2)} h`;
}

export function summarizeList(list) {
    if (!list || !list.length) {
        return {
            totalCleanTimeSec: 0,
            totalCleanArea: 0,
            taskCount: 0,
        };
    }
    return {
        totalCleanTimeSec: list.reduce(
            (s, i) => s + (Number(i.clean_time) || 0),
            0
        ),
        totalCleanArea: list.reduce(
            (s, i) => s + (Number(i.clean_area) || 0),
            0
        ),
        taskCount: list.length,
    };
}

/**
 * Campos da API que podem vir como string JSON (config, floor_list).
 * @returns {object|Array|null}
 */
export function parseCleaningReportJsonField(raw) {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'object') return raw;
    if (typeof raw !== 'string') return null;
    try {
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
}

/**
 * Unix (segundos) → data/hora local.
 */
export function formatCleaningReportUnixSeconds(
    unixSec,
    localeFormat = 'pt-BR'
) {
    const n = Number(unixSec);
    if (!Number.isFinite(n)) return '—';
    const d = new Date(n * 1000);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(localeFormat, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
}

/** Locale Redux (`pt_BR`) → BCP 47 para `Intl` / gráficos. */
export function localeToBcp47(code) {
    if (!code) return 'pt-BR';
    const map = { pt_BR: 'pt-BR', en_US: 'en-US', en_GB: 'en-GB' };
    return map[code] || 'pt-BR';
}

/** Mensagem de erro a partir de `err.response.data` (axios), p.ex. detalhe do modal. */
export function cleaningDetailErrorMessageFromPayload(payload) {
    if (payload && typeof payload === 'object' && payload.error) {
        return String(payload.error);
    }
    if (typeof payload === 'string') {
        return payload;
    }
    return '';
}

export function defaultCleaningReportDateRange() {
    const today = format(new Date(), 'yyyy-MM-dd');
    return {
        startDate: today,
        endDate: today,
    };
}

export function cleaningReportDefaultShortcut(startYmd, endYmd) {
    const until = new Date(`${endYmd}T12:00:00`);
    const from = new Date(`${startYmd}T12:00:00`);
    const today = new Date();
    if (Number.isNaN(until.getTime()) || Number.isNaN(from.getTime())) {
        return 'custom';
    }
    if (differenceInDays(until, today) !== 0) return 'custom';
    const diff = differenceInDays(until, from);
    if (diff === 0) return 'today';
    if (diff === 30 || diff === 60 || diff === 90) return `${diff}_days`;
    return 'custom';
}

/** IDs i18n das opções do seletor de métrica analisada (Horas → Meses). */
export const CLEANING_REPORT_CHART_PERIOD_MSG_IDS = [
    'page.robots.cleaning_report.chart.period.hours',
    'page.robots.cleaning_report.chart.period.weekdays',
    'page.robots.cleaning_report.chart.period.days',
    'page.robots.cleaning_report.chart.period.months',
];

/**
 * Cores alinhadas ao tema do pluginspace (ColorList).
 * @param {object} colors — resultado de `GetPluginspaceTheme(theme)`
 */
export function getCleaningReportChartColors(colors) {
    const graphColor = colors.dashboard_graphs || colors.primary || '#3f51b5';
    const cardColor =
        colors.dashboard_cards || colors.dashboard_graphs || '#3f51b5';
    const pieSegmentColors = [
        graphColor,
        cardColor,
        colors.secondary || '#E91E63',
        colors.primary || '#3f51b5',
    ];
    return { graphColor, cardColor, pieSegmentColors };
}

export function scrollCleaningReportTableIntoView(tableRef) {
    if (!tableRef || !tableRef.current) return;
    tableRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
    });
}

/**
 * Constrói a lista de dados a ser injetada no componente de exportação CSV.
 * Mapeia os campos da tabela e os ocultos requisitados.
 * @param {Array} list - Lista completa de limpezas (reportPayload.list)
 * @param {string} localeCode - Código do idioma (ex: 'pt_BR')
 * @param {boolean} includeRobotInfo - Se deve incluir o nome do robô (para Pluginspace)
 */
export function buildCleaningReportCsvData(
    list,
    localeCode = 'pt_BR',
    includeRobotInfo = false,
    isDetailed = false
) {
    if (!list || !Array.isArray(list)) {
        return [];
    }

    return list.map(item => {
        const rowData = {
            report_id: item.report_id != null ? String(item.report_id) : '',
            task_name: item.task_name || '—',
            clean_time_fmt: formatCleaningDuration(item.clean_time),
            efficiency:
                Number(item.clean_area || 0) > 0 &&
                Number(item.clean_time || 0) > 0
                    ? (
                          Number(item.clean_area) /
                          Math.max(1, Number(item.clean_time) / 60)
                      ).toFixed(2)
                    : 'N/A',
            clean_area: Number(item.clean_area || 0).toFixed(2),
            task_area: Number(item.task_area || 0).toFixed(2),
            create_time_fmt: formatCleaningReportCreateDateTime(
                item.end_time || item.start_time
            ),
            mode_label: getCleaningModeLabel(item.mode, localeCode),
            status_label: getCleaningTaskStatusLabel(item.status, localeCode),
        };

        if (includeRobotInfo) {
            const robotName =
                item._robot && item._robot.name ? item._robot.name : '';
            const robotCode =
                item._robot && item._robot.code ? ` (${item._robot.code})` : '';
            rowData.robot_name = `${robotName}${robotCode}`.trim() || '—';
        }

        if (isDetailed) {
            const bcp = localeToBcp47(localeCode);
            rowData.start_time_fmt = formatCleaningReportUnixSeconds(
                item.start_time,
                bcp
            );
            rowData.end_time_fmt = formatCleaningReportUnixSeconds(
                item.end_time,
                bcp
            );

            const completionPct =
                item.percentage != null ? Number(item.percentage) : 0;
            rowData.completion_pct = `${Math.round(
                Math.min(100, Math.max(0, completionPct))
            )}%`;

            let batteryPct = null;
            if (item.battery != null) {
                batteryPct = Number(item.battery);
            } else if (item.cost_battery != null) {
                batteryPct = Number(item.cost_battery);
            }
            rowData.battery_pct =
                batteryPct != null && !Number.isNaN(batteryPct)
                    ? `${Math.round(Math.min(100, Math.max(0, batteryPct)))}%`
                    : '—';
        }

        return rowData;
    });
}

/**
 * Retorna as configurações das colunas (cabeçalhos) para o CSV de Relatório de Limpeza.
 * @param {object} intl - Objeto intl para chamadas de formatação (injectIntl)
 * @param {boolean} includeRobotInfo - Se a coluna de robô deve ser inserida (para Pluginspace)
 * @param {boolean} isDetailed - Se as colunas de detalhamento extra devem ser incluídas
 */
export function getCleaningReportCsvHeaders(
    intl,
    includeRobotInfo = false,
    isDetailed = false
) {
    const headers = [
        {
            label: intl.formatMessage({
                id: 'page.robots.cleaning_report.csv.report_id',
                defaultMessage: 'Report ID',
            }),
            key: 'report_id',
        },
    ];

    if (includeRobotInfo) {
        headers.push({
            label: intl.formatMessage({
                id: 'page.robots.cleaning_report.csv.col_robot',
                defaultMessage: 'Robot',
            }),
            key: 'robot_name',
        });
    }

    headers.push(
        {
            label: intl.formatMessage({
                id: 'page.robots.cleaning_report.csv.col_task',
                defaultMessage: 'Task',
            }),
            key: 'task_name',
        },
        {
            label: intl.formatMessage({
                id: 'page.robots.cleaning_report.csv.col_clean_time',
                defaultMessage: 'Clean Time',
            }),
            key: 'clean_time_fmt',
        },
        {
            label: 'Eficiência (m²/min)',
            key: 'efficiency',
        },
        {
            label: intl.formatMessage({
                id: 'page.robots.cleaning_report.csv.col_area',
                defaultMessage: 'Clean Area',
            }),
            key: 'clean_area',
        },
        {
            label: intl.formatMessage({
                id: 'page.robots.cleaning_report.csv.col_created',
                defaultMessage: 'Created At',
            }),
            key: 'create_time_fmt',
        },
        {
            label: intl.formatMessage({
                id: 'page.robots.cleaning_report.csv.col_mode',
                defaultMessage: 'Mode',
            }),
            key: 'mode_label',
        },
        {
            label: intl.formatMessage({
                id: 'page.robots.cleaning_report.csv.col_status',
                defaultMessage: 'Status',
            }),
            key: 'status_label',
        }
    );

    if (isDetailed) {
        headers.push(
            {
                label: intl.formatMessage({
                    id: 'page.robots.cleaning_report.detail.metric.start',
                    defaultMessage: 'Start Time',
                }),
                key: 'start_time_fmt',
            },
            {
                label: intl.formatMessage({
                    id: 'page.robots.cleaning_report.detail.metric.end',
                    defaultMessage: 'End Time',
                }),
                key: 'end_time_fmt',
            },
            {
                label: intl.formatMessage({
                    id: 'page.robots.cleaning_report.detail.completion',
                    defaultMessage: 'Completion (%)',
                }),
                key: 'completion_pct',
            },
            {
                label: intl.formatMessage({
                    id:
                        'page.robots.cleaning_report.detail.battery_consumption',
                    defaultMessage: 'Battery Consumption',
                }),
                key: 'battery_pct',
            }
        );
    }

    return headers;
}

/**
 * Série para gráfico de tempo de limpeza com agregação por período
 * (mesmo modelo de granularidade do card de ROI).
 */
export function buildCleanTimeChartData(
    cleaningReports,
    periodIdx,
    periodDates,
    localeCode = 'pt_BR'
) {
    const list = cleaningReports || [];
    const localeFormat = localeToBcp47(localeCode);

    if (periodIdx === 0) {
        const hourlyTime = new Array(24).fill(0);
        list.forEach(item => {
            const h = extractHourFromCreateTime(
                getCleaningReportTaskStartTime(item)
            );
            if (h != null && h >= 0 && h <= 23) {
                hourlyTime[h] += Number(item.clean_time) || 0;
            }
        });
        return scaleCleanTimeChartPoints(
            Array.from({ length: 24 }, (_, i) => ({
                name: `${i}h`,
                secondsTotal: hourlyTime[i],
            }))
        );
    }

    if (periodIdx === 1) {
        const dowTime = new Array(7).fill(0);
        list.forEach(item => {
            const dayKey = getCleaningReportDayKey(
                getCleaningReportTaskStartTime(item)
            );
            if (dayKey !== '—') {
                const [y, m, d] = dayKey.split('-');
                const dObj = new Date(Number(y), Number(m) - 1, Number(d));
                dowTime[dObj.getDay()] += Number(item.clean_time) || 0;
            }
        });
        return scaleCleanTimeChartPoints(
            getCleaningReportWeekDayLabels(localeFormat).map((label, i) => ({
                name: label,
                secondsTotal: dowTime[i],
            }))
        );
    }

    if (periodIdx === 2) {
        const byDayTime = {};

        if (periodDates && periodDates.startDate && periodDates.endDate) {
            const parseDateString = dStr => {
                const parts = dStr.split('T')[0].split('-');
                return new Date(
                    Number(parts[0]),
                    Number(parts[1]) - 1,
                    Number(parts[2])
                );
            };
            const current = parseDateString(periodDates.startDate);
            const end = parseDateString(periodDates.endDate);
            let safety = 0;
            while (current <= end && safety < 1000) {
                const y = current.getFullYear();
                const m = String(current.getMonth() + 1).padStart(2, '0');
                const day = String(current.getDate()).padStart(2, '0');
                byDayTime[`${y}-${m}-${day}`] = 0;
                current.setDate(current.getDate() + 1);
                safety += 1;
            }
        }

        list.forEach(item => {
            const dayKey = getCleaningReportDayKey(
                getCleaningReportTaskStartTime(item)
            );
            if (dayKey !== '—') {
                byDayTime[dayKey] =
                    (byDayTime[dayKey] || 0) + (Number(item.clean_time) || 0);
            }
        });

        const sortedDays = Object.keys(byDayTime).sort();
        return scaleCleanTimeChartPoints(
            sortedDays.map(key => {
                const [y, m, d] = key.split('-');
                const dateObj = new Date(Number(y), Number(m) - 1, Number(d));
                return {
                    name: dateObj.toLocaleDateString(localeFormat, {
                        day: '2-digit',
                        month: '2-digit',
                    }),
                    secondsTotal: byDayTime[key],
                };
            })
        );
    }

    const monthTime = new Array(12).fill(0);
    list.forEach(item => {
        const dayKey = getCleaningReportDayKey(
            getCleaningReportTaskStartTime(item)
        );
        if (dayKey !== '—') {
            const m = Number(dayKey.split('-')[1]);
            monthTime[m - 1] += Number(item.clean_time) || 0;
        }
    });
    return scaleCleanTimeChartPoints(
        getCleaningReportMonthLabels(localeFormat).map((label, i) => ({
            name: label,
            secondsTotal: monthTime[i],
        }))
    );
}
